import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, Printer, X } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

const fmt$ = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ROYALTY_PER_VIAL = 2.75; // Robert's per-vial royalty
const day = (iso) => (iso ? String(iso).slice(0, 10) : "—");
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

/** v414.36 — printable payout statement for the Store (Ontario Master):
 *  orders + spread per payout period, opened as a clean print window so it
 *  can be printed or saved as PDF and handed to the Store. */
export function PayoutStatementDialog({ onClose }) {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [status, setStatus] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const emailStatement = async () => {
    if (!data) return;
    if (!window.confirm(
      `Email this statement (${data.totals.count} orders, spread ${fmt$(data.totals.spread)}) to ${data.account_name}'s inbox?\n\nThe emailed copy shows orders, wholesale and spread only — the vial royalty and verify flags stay internal.`)) return;
    setEmailing(true);
    try {
      const { data: res } = await api.post("/partner-admin/payout-statement/email",
        { from, to, status });
      toast.success(`Statement sent to ${res.sent_to.join(", ")}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailing(false);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/partner-admin/payout-statement", {
        params: { from, to, status },
      });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, [from, to, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const print = () => {
    if (!data) return;
    const rows = data.rows.map((r) => `
      <tr>
        <td>${day(r.received_at)}</td>
        <td class="mono">${r.reference || r.order_ref || ""}</td>
        <td>${(r.received_from || "").replace(/</g, "&lt;")}</td>
        <td class="num">${fmt$(r.gross_amount)}</td>
        <td class="num">${fmt$(r.wholesale_basis)}</td>
        <td class="num">${Number(r.vials || 0)}</td>
        <td class="num bold">${fmt$(r.distributor_credit)}${(r.note || "").includes("NO WHOLESALE FOUND") ? ' <span class="warn">⚠ verify</span>' : ""}</td>
        <td>${r.status === "paid_out" ? `Paid out ${day(r.paid_out_at)}` : "Pending"}</td>
      </tr>`).join("");
    const t = data.totals;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Payout statement — ${data.account_name}</title>
<style>
  body{font-family:Georgia,'Times New Roman',serif;color:#18181b;margin:40px;}
  h1{font-size:20px;margin:0;} .sub{color:#71717a;font-size:12px;margin:4px 0 24px;}
  table{width:100%;border-collapse:collapse;font-size:12px;}
  th{text-align:left;border-bottom:2px solid #c9a24a;padding:6px 8px;color:#71717a;font-weight:normal;text-transform:uppercase;letter-spacing:.05em;font-size:10px;}
  td{border-bottom:1px solid #e4e4e7;padding:6px 8px;vertical-align:top;}
  .num{text-align:right;font-variant-numeric:tabular-nums;} .bold{font-weight:bold;}
  .mono{font-family:ui-monospace,Menlo,monospace;font-size:11px;}
  th.num{text-align:right;}
  .totals{margin-top:16px;width:auto;margin-left:auto;font-size:13px;}
  .totals td{border:none;padding:3px 8px;}
  .grand{font-size:15px;font-weight:bold;border-top:2px solid #18181b !important;}
  .foot{margin-top:32px;color:#a1a1aa;font-size:10px;}
  .warn{color:#b45309;font-size:9px;font-weight:bold;}
</style></head><body>
  <h1>Payout statement — ${data.account_name}</h1>
  <div class="sub">Period: ${from} to ${to} · ${status === "all" ? "all payouts" : status === "paid_out" ? "paid out only" : "pending only"} · generated ${day(data.generated_at)} by ReUP</div>
  <table>
    <thead><tr><th>Paid date</th><th>Order ref</th><th>Buyer</th><th class="num">Products</th><th class="num">Wholesale</th><th class="num">Vials</th><th class="num">Spread owed</th><th>Payout</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="8" style="color:#a1a1aa;">No orders in this period.</td></tr>'}</tbody>
  </table>
  <table class="totals">
    <tr><td>Orders</td><td class="num">${t.count}</td></tr>
    <tr><td>Products total</td><td class="num">${fmt$(t.products)}</td></tr>
    <tr><td>Wholesale total</td><td class="num">${fmt$(t.wholesale)}</td></tr>
    <tr><td>Spread paid out</td><td class="num">${fmt$(t.paid_spread)}</td></tr>
    <tr><td>Spread pending</td><td class="num">${fmt$(t.pending_spread)}</td></tr>
    <tr class="grand"><td class="grand">Total spread</td><td class="num grand">${fmt$(t.spread)}</td></tr>
    <tr><td>Vials sold</td><td class="num">${t.vials || 0}</td></tr>
    <tr><td>Vial royalty @ ${fmt$(ROYALTY_PER_VIAL)}/vial</td><td class="num bold">${fmt$((t.vials || 0) * ROYALTY_PER_VIAL)}</td></tr>
  </table>
  <div class="foot">Spread per order = products the customer paid (after store discounts, shipping excluded) minus the Store's special wholesale pricing. Questions: reply to your ReUP contact.</div>
<script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.write(html);
    w.document.close();
  };

  // v414.39 — Robert's royalty statement: vials sold + $2.75/vial owed,
  // printable for the same period/status selection.
  const printRoyalty = () => {
    if (!data) return;
    const vialRows = data.rows.filter((r) => Number(r.vials || 0) > 0);
    const rows = vialRows.map((r) => `
      <tr>
        <td>${day(r.received_at)}</td>
        <td class="mono">${r.reference || r.order_ref || ""}</td>
        <td class="num">${Number(r.vials || 0)}</td>
        <td class="num bold">${fmt$(Number(r.vials || 0) * ROYALTY_PER_VIAL)}</td>
        <td>${r.status === "paid_out" ? "Settled order" : "Pending order"}</td>
      </tr>`).join("");
    const totalVials = data.totals.vials || 0;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Royalty statement — Robert</title>
<style>
  body{font-family:Georgia,'Times New Roman',serif;color:#18181b;margin:40px;}
  h1{font-size:20px;margin:0;} .sub{color:#71717a;font-size:12px;margin:4px 0 24px;}
  table{width:100%;border-collapse:collapse;font-size:12px;}
  th{text-align:left;border-bottom:2px solid #c9a24a;padding:6px 8px;color:#71717a;font-weight:normal;text-transform:uppercase;letter-spacing:.05em;font-size:10px;}
  td{border-bottom:1px solid #e4e4e7;padding:6px 8px;vertical-align:top;}
  .num{text-align:right;font-variant-numeric:tabular-nums;} .bold{font-weight:bold;}
  .mono{font-family:ui-monospace,Menlo,monospace;font-size:11px;}
  th.num{text-align:right;}
  .totals{margin-top:16px;width:auto;margin-left:auto;font-size:13px;}
  .totals td{border:none;padding:3px 8px;}
  .grand{font-size:15px;font-weight:bold;border-top:2px solid #18181b !important;}
  .foot{margin-top:32px;color:#a1a1aa;font-size:10px;}
</style></head><body>
  <h1>Royalty statement — Robert</h1>
  <div class="sub">Period: ${from} to ${to} · rate ${fmt$(ROYALTY_PER_VIAL)} per vial · generated ${day(data.generated_at)} by ReUP</div>
  <table>
    <thead><tr><th>Paid date</th><th>Order ref</th><th class="num">Vials</th><th class="num">Royalty</th><th>Order status</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="5" style="color:#a1a1aa;">No vial sales in this period.</td></tr>'}</tbody>
  </table>
  <table class="totals">
    <tr><td>Orders with vials</td><td class="num">${vialRows.length}</td></tr>
    <tr><td>Total vials sold</td><td class="num">${totalVials}</td></tr>
    <tr class="grand"><td class="grand">Royalty owed</td><td class="num grand">${fmt$(totalVials * ROYALTY_PER_VIAL)}</td></tr>
  </table>
  <div class="foot">Royalty = vials sold on paid Active Bio Labs orders × ${fmt$(ROYALTY_PER_VIAL)} per vial. Nasal mists, creams and non-vial items are excluded.</div>
<script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.write(html);
    w.document.close();
  };

  const t = data?.totals;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="payout-statement-dialog">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Payout statement</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="payout-statement-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <label className="text-xs font-semibold">
            From
            <BrutalInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="statement-from" />
          </label>
          <label className="text-xs font-semibold">
            To
            <BrutalInput type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="statement-to" />
          </label>
          <label className="text-xs font-semibold">
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="block h-10 rounded-lg border-2 border-zinc-300 px-2 text-sm font-medium bg-white"
              data-testid="statement-status">
              <option value="all">All</option>
              <option value="pending_payout">Pending only</option>
              <option value="paid_out">Paid out only</option>
            </select>
          </label>
          <BrutalButton size="sm" onClick={print} disabled={busy || !data} data-testid="statement-print">
            <Printer size={15} weight="bold" /> Print / PDF
          </BrutalButton>
          <BrutalButton size="sm" variant="outline" onClick={emailStatement}
            disabled={busy || emailing || !data} data-testid="statement-email">
            <PaperPlaneTilt size={15} weight="bold" /> {emailing ? "Sending…" : "Email to Store"}
          </BrutalButton>
          <BrutalButton size="sm" variant="secondary" onClick={printRoyalty}
            disabled={busy || !data} data-testid="statement-royalty-print"
            title="Printable statement of vials sold and royalty owed for this period">
            <Printer size={15} weight="bold" /> Robert royalty
          </BrutalButton>
        </div>
        {t && (
          <p className="text-xs text-zinc-500 mb-2" data-testid="statement-summary">
            {t.count} order(s) · products {fmt$(t.products)} · wholesale {fmt$(t.wholesale)} ·{" "}
            <span className="font-bold text-zinc-900">spread {fmt$(t.spread)}</span>{" "}
            ({fmt$(t.pending_spread)} pending / {fmt$(t.paid_spread)} paid out)
            <span className="block mt-0.5" data-testid="statement-vials">
              <span className="font-bold text-zinc-900">{t.vials || 0} vials sold</span>
              {" · "}vial royalty @ {fmt$(ROYALTY_PER_VIAL)}/vial ={" "}
              <span className="font-bold text-zinc-900">{fmt$((t.vials || 0) * ROYALTY_PER_VIAL)}</span>
            </span>
          </p>
        )}
        {data?.rows?.some((r) => (r.note || "").includes("NO WHOLESALE FOUND")) && (
          <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2" data-testid="statement-verify-warning">
            Some orders couldn't be priced against a wholesale card (⚠ verify) — their spread may be overstated. Check the order lines before paying out.
          </p>
        )}
        <div className="border border-zinc-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-zinc-400 uppercase tracking-wide text-[10px]">
                <th className="px-2 py-1.5">Date</th>
                <th className="px-2 py-1.5">Order</th>
                <th className="px-2 py-1.5 text-right">Products</th>
                <th className="px-2 py-1.5 text-right">Vials</th>
                <th className="px-2 py-1.5 text-right">Spread</th>
                <th className="px-2 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r) => (
                <tr key={r.id} className="border-t border-zinc-100" data-testid={`statement-row-${r.order_ref}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{day(r.received_at)}</td>
                  <td className="px-2 py-1.5 font-mono">{r.order_ref}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmt$(r.gross_amount)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Number(r.vials || 0)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                    {fmt$(r.distributor_credit)}
                    {(r.note || "").includes("NO WHOLESALE FOUND") && (
                      <span className="ml-1 text-[9px] font-bold text-amber-700" data-testid={`statement-verify-${r.order_ref}`}>⚠</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{r.status === "paid_out" ? "Paid out" : "Pending"}</td>
                </tr>
              ))}
              {data && !data.rows.length && (
                <tr><td colSpan={6} className="px-2 py-3 text-zinc-400">No orders in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
